import { apiClient } from './apiService'

export interface PMLead {
  id: string
  name: string
  displayName: string
  email?: string | null
}

export interface PMLeadStats {
  name: string
  total: number
  statuses: Record<string, number>
  priorities: Record<string, number>
}

export interface PMLeadStatsResponse {
  success: boolean
  data: {
    leads: PMLeadStats[]
    unassigned: number
    totalInitiatives: number
    totalLeads: number
  }
  message: string
  error?: string
}

export interface PMLeadResponse {
  success: boolean
  data: PMLead[]
  total: number
  message: string
  error?: string
}

export interface PMLeadInitiative {
  key: string
  summary: string
  status: string
  priority: string
  pmLead: string
}

export interface PMLeadInitiativesResponse {
  success: boolean
  data: PMLeadInitiative[]
  total: number
  lead: string
  message: string
  error?: string
}

class PMLeadService {
  private baseUrl = '/pm-leads'

  /**
   * Get all PM Leads
   */
  async getAllPMLeads(jql?: string): Promise<PMLeadResponse> {
    try {
      const params = jql ? { jql } : {}
      const response = await apiClient.get<PMLeadResponse>(this.baseUrl, { params })
      return response.data
    } catch (error) {
      console.error('Error fetching PM Leads:', error)
      return {
        success: false,
        data: [],
        total: 0,
        message: 'Failed to fetch PM Leads',
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Search PM Leads by name or email
   */
  async searchPMLeads(searchTerm: string): Promise<PMLeadResponse> {
    try {
      if (!searchTerm.trim()) {
        return this.getAllPMLeads()
      }

      const response = await apiClient.get<PMLeadResponse>(`${this.baseUrl}/search`, {
        params: { q: searchTerm }
      })
      return response.data
    } catch (error) {
      console.error(`Error searching PM Leads for "${searchTerm}":`, error)
      return {
        success: false,
        data: [],
        total: 0,
        message: `Failed to search PM Leads for "${searchTerm}"`,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Get PM Lead usage statistics
   */
  async getPMLeadStats(): Promise<PMLeadStatsResponse> {
    try {
      const response = await apiClient.get<PMLeadStatsResponse>(`${this.baseUrl}/stats`)
      return response.data
    } catch (error) {
      console.error('Error fetching PM Lead statistics:', error)
      return {
        success: false,
        data: {
          leads: [],
          unassigned: 0,
          totalInitiatives: 0,
          totalLeads: 0
        },
        message: 'Failed to fetch PM Lead statistics',
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Get initiatives for a specific PM Lead
   */
  async getInitiativesForPMLead(leadName: string): Promise<PMLeadInitiativesResponse> {
    try {
      const encodedName = encodeURIComponent(leadName)
      const response = await apiClient.get<PMLeadInitiativesResponse>(`${this.baseUrl}/${encodedName}/initiatives`)
      return response.data
    } catch (error) {
      console.error(`Error fetching initiatives for PM Lead "${leadName}":`, error)
      return {
        success: false,
        data: [],
        total: 0,
        lead: leadName,
        message: `Failed to fetch initiatives for PM Lead "${leadName}"`,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Clear PM Lead cache
   */
  async clearCache(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>(`${this.baseUrl}/cache/clear`)
      return response.data
    } catch (error) {
      console.error('Error clearing PM Lead cache:', error)
      return {
        success: false,
        message: 'Failed to clear PM Lead cache'
      }
    }
  }

  /**
   * Filter PM Leads by search term (client-side)
   */
  filterPMLeads(leads: PMLead[], searchTerm: string): PMLead[] {
    if (!searchTerm.trim()) {
      return leads
    }

    const term = searchTerm.toLowerCase()
    return leads.filter(lead => 
      lead.name.toLowerCase().includes(term) ||
      lead.displayName.toLowerCase().includes(term) ||
      lead.id.includes(term) ||
      (lead.email && lead.email.toLowerCase().includes(term))
    )
  }

  /**
   * Sort PM Leads by name
   */
  sortPMLeads(leads: PMLead[], ascending: boolean = true): PMLead[] {
    return [...leads].sort((a, b) => {
      const comparison = a.name.localeCompare(b.name)
      return ascending ? comparison : -comparison
    })
  }

  /**
   * Get unique PM Lead names from a list
   */
  getUniquePMLeadNames(leads: PMLead[]): string[] {
    const uniqueNames = new Set(leads.map(lead => lead.name))
    return Array.from(uniqueNames).sort()
  }

  /**
   * Group PM Leads by first letter
   */
  groupPMLeadsByLetter(leads: PMLead[]): Record<string, PMLead[]> {
    const grouped: Record<string, PMLead[]> = {}
    
    leads.forEach(lead => {
      const firstLetter = lead.name.charAt(0).toUpperCase()
      if (!grouped[firstLetter]) {
        grouped[firstLetter] = []
      }
      grouped[firstLetter].push(lead)
    })

    // Sort PM Leads within each group
    Object.keys(grouped).forEach(letter => {
      grouped[letter] = this.sortPMLeads(grouped[letter])
    })

    return grouped
  }

  /**
   * Validate PM Lead name
   */
  isValidPMLeadName(name: string): boolean {
    return name && name.trim().length > 0 && name.trim().length <= 100
  }

  /**
   * Normalize PM Lead name for comparison
   */
  normalizePMLeadName(name: string): string {
    return name.trim().toLowerCase().replace(/[^a-z0-9]/g, '_')
  }

  /**
   * Check if two PM Leads are the same
   */
  arePMLeadsEqual(lead1: PMLead, lead2: PMLead): boolean {
    return this.normalizePMLeadName(lead1.name) === this.normalizePMLeadName(lead2.name)
  }

  /**
   * Get PM Lead color based on name (for UI consistency)
   */
  getPMLeadColor(leadName: string): string {
    // Simple hash-based color generation for consistent PM Lead colors
    let hash = 0
    for (let i = 0; i < leadName.length; i++) {
      hash = leadName.charCodeAt(i) + ((hash << 5) - hash)
    }
    
    const hue = Math.abs(hash) % 360
    return `hsl(${hue}, 65%, 50%)`
  }

  /**
   * Format PM Lead name for display
   */
  formatPMLeadForDisplay(lead: PMLead): string {
    if (!lead.name) return 'Unassigned'
    
    // Return the display name if available, otherwise format the name
    if (lead.displayName && lead.displayName !== lead.name) {
      return lead.displayName
    }
    
    // Capitalize first letter of each word
    return lead.name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  /**
   * Extract initials from PM Lead name
   */
  getPMLeadInitials(lead: PMLead): string {
    const name = lead.displayName || lead.name
    if (!name) return 'U'
    
    const words = name.split(' ').filter(word => word.length > 0)
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase()
    }
    
    return words
      .slice(0, 2) // Take first two words
      .map(word => word.charAt(0).toUpperCase())
      .join('')
  }

  /**
   * Check if PM Lead has email
   */
  hasEmail(lead: PMLead): boolean {
    return !!(lead.email && lead.email.trim().length > 0)
  }

  /**
   * Get PM Lead workload level based on initiative count
   */
  getWorkloadLevel(initiativeCount: number): 'low' | 'medium' | 'high' | 'overloaded' {
    if (initiativeCount === 0) return 'low'
    if (initiativeCount <= 2) return 'medium'
    if (initiativeCount <= 4) return 'high'
    return 'overloaded'
  }

  /**
   * Get workload color based on level
   */
  getWorkloadColor(level: 'low' | 'medium' | 'high' | 'overloaded'): string {
    const colors = {
      low: '#4caf50',      // Green
      medium: '#ff9800',   // Orange
      high: '#f44336',     // Red
      overloaded: '#9c27b0' // Purple
    }
    return colors[level]
  }
}

export const pmLeadService = new PMLeadService()
export default pmLeadService
